import React, { useContext, useState, useEffect } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import "../../App.css";
import { UserContext } from "../context/UserContext"; // Assuming you have a UserContext
import Popup2 from "./Popup2";
import axios from "axios";
import { FaExternalLinkAlt } from "react-icons/fa";
import Quiz from './Quiz';

const ItemType = {
  CATEGORY: "CATEGORY",
};

const DraggableCategory = ({ category, boxIndex, onSelect }) => {
  const [isDragging, setIsDragging] = useState(false);
  let clickTimer = null;

  const [{ isCurrentlyDragging }, drag] = useDrag({
    type: ItemType.CATEGORY,
    item: { category, boxIndex },
    collect: (monitor) => ({
      isCurrentlyDragging: !!monitor.isDragging(),
    }),
    end: () => {
      setTimeout(() => setIsDragging(false), 100);
    },
  });

  const handleClick = () => {
    if (!isDragging && boxIndex === null) {
      onSelect(category);
    }
  };

  const handleMouseDown = () => {
    clickTimer = setTimeout(() => setIsDragging(true), 200);
  };

  const handleMouseUp = () => {
    clearTimeout(clickTimer);
    setIsDragging(false);
  };

  return (
    <div
      ref={drag}
      style={{
        padding: "10px",
        border: "1px solid #ccc",
        borderRadius: "5px",
        backgroundColor: isCurrentlyDragging ? "#e0e0e0" : "#f9f9f9",
        cursor: "move",
        marginBottom: "5px",
        userSelect: "none",
      }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
    >
      {category}
    </div>
  );
};

const DroppableBox = ({ box, boxIndex, onDrop }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemType.CATEGORY,
    drop: (item) => onDrop(item.category, item.boxIndex, boxIndex),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));

  return (
    <div
      ref={drop}
      style={{
        border: "1px dashed #ccc",
        borderRadius: "5px",
        minHeight: "50px",
        padding: "5px",
        backgroundColor: isOver ? "#e0e0e0" : "#f1f1f1",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: "-20px",
          left: "10px",
          fontWeight: "bold",
        }}
      >
        {boxIndex + 1}.
      </span>
      {box.map((item, index) => (
        <DraggableCategory
          key={index}
          category={item}
          boxIndex={boxIndex}
          onSelect={() => {}}
        />
      ))}
    </div>
  );
};

function Popup1({ onClose, noDndProvider = false }) {
  const [showPopup2, setShowPopup2] = useState(false);
  const { emailId, state } = useContext(UserContext);
  const [isFirstTime, setIsFirstTime] = useState(false);
  // const [selectedType, setSelectedType] = useState("");
  // setSelectedType(state);
  const initialCategories = [
    "Affordable housing",
    "Better business conditions",
    "Better education",
    "Crime reduction & law enforcement",
    "Defence and security",
    "Economic stability",
    "Environmental and Energy Issues",
    "Fair workplaces",
    "Family and children’s needs",
    "Health and hospitals",
    "Honest government",
    "Increasing migration",
    "Lower taxes for families",
    "Rural needs",
    "Reducing unemployment",
    "Reducing migration",
    "Support for elderly"
  ];
  const [categories, setCategories] = useState(initialCategories);
  const [dropBoxes, setDropBoxes] = useState(Array(17).fill([]));
  const [hasDataInDB, setHasDataInDB] = useState(false); // New state for checking database data
  const [suggestionInput, setSuggestionInput] = useState("");
  const [showQuiz, setShowQuiz] = useState(false);

  const handleCategoryClick = (category) => {
    const emptyBoxIndex = dropBoxes.findIndex((box) => box.length === 0);

    if (emptyBoxIndex !== -1) {
      setDropBoxes((prevDropBoxes) => {
        const updatedDropBoxes = [...prevDropBoxes];
        updatedDropBoxes[emptyBoxIndex] = [category];
        return updatedDropBoxes;
      });

      setCategories((prevCategories) =>
        prevCategories.filter((item) => item !== category)
      );
    } else {
      alert("All boxes are filled. Please clear a box to add more.");
    }
  };

  // Check if at least 3 categories are selected
  const hasMinimumCategories = dropBoxes.filter(box => box.length > 0).length >= 3;

  const handleDrop = (category, sourceBoxIndex, targetBoxIndex) => {
    setDropBoxes((prevDropBoxes) => {
      const updatedDropBoxes = [...prevDropBoxes];

      if (updatedDropBoxes[targetBoxIndex].length === 0) {
        if (sourceBoxIndex !== null) {
          updatedDropBoxes[sourceBoxIndex] = updatedDropBoxes[sourceBoxIndex].filter(
            (item) => item !== category
          );
        }

        updatedDropBoxes[targetBoxIndex] = [category];

        if (sourceBoxIndex === null) {
          setCategories((prevCategories) =>
            prevCategories.filter((item) => item !== category)
          );
        }
      } else {
        alert("Please press reset to rearrange the categories.");
      }

      return updatedDropBoxes;
    });
  };

  const handleReset = () => {
    setDropBoxes(Array(17).fill([]));
    setCategories(initialCategories);
  };

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await fetch(
          `/api/preferences?email_id=${emailId}`
        );
  
        if (response.ok) {
          const data = await response.json();
          const fetchedPreferences = data.preferences;
          
          if (fetchedPreferences.length > 0) {
            setHasDataInDB(true); // ✅ Mark user as having saved data
          }
  
          // Pre-fill the drop boxes based on fetched preferences
          setDropBoxes((prevDropBoxes) => {
            const updatedDropBoxes = [...prevDropBoxes];
            fetchedPreferences.forEach((pref) => {
              updatedDropBoxes[pref.ranking - 1] = [pref.category_name];
            });
            return updatedDropBoxes;
          });
  
          // Remove already ranked categories from the left-hand list
          setCategories((prevCategories) =>
            prevCategories.filter(
              (category) =>
                !fetchedPreferences.some(
                  (pref) => pref.category_name === category
                )
            )
          );
        } else if (response.status === 404) {
          console.log("No preferences found for this user."); // Log message instead of alert
          // No need to set drop boxes or categories as no preferences are found
        } else {
          console.error("Failed to fetch preferences");
        }
      } catch (error) {
        console.error("Error fetching preferences:", error);
      }
    };
  
    fetchPreferences();
  }, [emailId]);

  useEffect(() => {
    const checkIfFirstTime = async () => {
      try {
        const response = await axios.get(
          "/api/check-firsttime",
          {
            params: { email_id: emailId },
          }
        );
        setIsFirstTime(response.data.firsttime === 1);
      } catch (error) {
        console.error("Error checking first time:", error);
      }
    };
    
    checkIfFirstTime();
  }, [emailId]);

  const handleNextClick = async () => {
    const filledBoxesCount = dropBoxes.filter(box => box.length > 0).length;
    if (filledBoxesCount < 3) {
        alert("Please select at least 3 categories before proceeding.");
        return;
    }

    try {
        const preferences = dropBoxes
          .filter(box => box.length > 0)  // Only include filled boxes
          .map((box, index) => ({
            category_name: box[0],
            ranking: index + 1,
          }));

        const payload = {
            email_id: emailId,
            preferences,
        };

        const response = await fetch("/api/preferences/update", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        if (response.ok) {
            // Update firsttime status if this is the user's first time
            if (isFirstTime) {
                try {
                    const updateFirstTimeResponse = await fetch("/api/update-firsttime", {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ email_id: emailId }),
                    });
                    
                    if (!updateFirstTimeResponse.ok) {
                        console.error("Failed to update firsttime status");
                    }
                } catch (error) {
                    console.error("Error updating firsttime status:", error);
                }
            }
            
            setShowPopup2(true); // Opens Popup2
        } else {
            const errorData = await response.json();
            alert(
                errorData.error ||
                    "Failed to save your preferences. Please try again."
            );
        }
    } catch (error) {
        alert("Failed to save your preferences. Please try again.");
    }
};

  const handleSubmit = async () => {
    const filledBoxesCount = dropBoxes.filter(box => box.length > 0).length;
    if (filledBoxesCount < 3) {
        alert("Please select at least 3 categories before submitting.");
        return;
    }

    try {
      const preferences = dropBoxes
        .filter(box => box.length > 0)  // Only include filled boxes
        .map((box, index) => ({
          category_name: box[0],
          ranking: index + 1,
        }));

      const payload = {
        email_id: emailId,
        preferences,
      };

      const response = await fetch("/api/preferences/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        alert("Your preferences have been submitted successfully!If you are a candidate, please go to the drop down menu and click on 'category preference / candidate verification' page to finish up verifying your candidature.");
        onClose();
        // Redirect to root path after closing
        window.location.href = "/main";
      } else {
        const errorData = await response.json();
        console.error("Error submitting preferences:", errorData);
        alert(
          errorData.error || "Failed to submit your preferences. Please try again."
        );
      }
    } catch (error) {
      console.error("Error submitting preferences:", error);
      alert("Failed to submit your preferences. Please try again.");
    }
  };

  // Add function to handle category suggestion submission
  const handleSuggestionSubmit = async () => {
    if (!suggestionInput.trim()) {
      alert("Please enter a category suggestion");
      return;
    }

    try {
      const response = await fetch("/api/category-suggestions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email_id: emailId,
          category_name: suggestionInput.trim()
        }),
      });

      if (response.ok) {
        alert("Thank you for your suggestion!");
        setSuggestionInput(""); // Clear input after successful submission
      } else {
        alert("Failed to submit suggestion. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting suggestion:", error);
      alert("Failed to submit suggestion. Please try again.");
    }
  };

  // Render the content of the component
  const renderContent = () => (
    <>
      {!showPopup2 ? (
        <div
          className="popup-section"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 999,
          }}
        >
          <div
            className="popup-container"
            style={{
              backgroundColor: "#fff",
              borderRadius: "10px",
              padding: "20px",
              width: "90%",
              maxWidth: "900px",
              boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
              position: "relative",
              overflowY: "auto",
              maxHeight: "90vh",
            }}
          >
            <div className="popup popup-1">
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '20px'
              }}>
                <button
                  style={{
                    padding: '12px 25px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                  onClick={async () => {
                    const filledBoxesCount = dropBoxes.filter(box => box.length > 0).length;
                    if (filledBoxesCount < 3) {
                      alert("Please select at least 3 categories before proceeding.");
                      return;
                    }

                    try {
                      const preferences = dropBoxes
                        .filter(box => box.length > 0)
                        .map((box, index) => ({
                          category_name: box[0],
                          ranking: index + 1,
                        }));

                      const payload = {
                        email_id: emailId,
                        preferences,
                      };

                      const response = await fetch("/api/preferences/update", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(payload),
                      });

                      if (response.ok) {
                        // Update firsttime status if this is the user's first time
                        if (isFirstTime) {
                          try {
                            const updateFirstTimeResponse = await fetch("/api/update-firsttime", {
                              method: "PATCH",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ email_id: emailId }),
                            });
                            
                            if (!updateFirstTimeResponse.ok) {
                              console.error("Failed to update firsttime status");
                            }
                          } catch (error) {
                            console.error("Error updating firsttime status:", error);
                          }
                        }
                        
                        setShowQuiz(true);
                      } else {
                        const errorData = await response.json();
                        alert(errorData.error || "Failed to save your preferences. Please try again.");
                      }
                    } catch (error) {
                      alert("Failed to save your preferences. Please try again.");
                    }
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#c82333';
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#dc3545';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  Click to Quiz
                </button>
              </div>
              <h3 style={{ textAlign: "center", marginBottom: "10px" }}>
                What's important to you?
              </h3>
  
              <div className="categories-section" style={{ marginTop: "10px" }}>
                <h4
                  style={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    textAlign: "center",
                    marginBottom: "10px",
                  }}
                >
                  Select the Category according to your preference(Minimum 3):
                </h4>
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "20px"
                }}>
                  <p style={{
                    color: "red",
                    textAlign: "center",
                    fontSize: "14px",
                    margin: 0
                  }}>
                    You can click to select or simply drag and drop to set your category preferences.
                  </p>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    fontSize: "14px"
                  }}>
                    <span>The categories listed are based on the reference provided by the</span>
                    <a 
                      href="https://theconversation.com/less-about-climate-change-more-on-reducing-migration-heres-what-matters-most-to-australian-voters-236129"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "black",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center"
                      }}
                    >
                      <FaExternalLinkAlt size={16} />
                    </a>
                  </div>
                </div>
                <div
                  className="categories-boxes-wrapper"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "20px",
                  }}
                >
                  <div
                    className="categories-wrapper"
                    style={{
                      flex: "1",
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {categories.map((category, index) => (
                      <DraggableCategory
                        key={index}
                        category={category}
                        boxIndex={null}
                        onSelect={handleCategoryClick}
                      />
                    ))}
                  </div>
  
                  <div
                    className="drag-drop-boxes"
                    style={{
                      flex: "1",
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {dropBoxes.map((box, boxIndex) => (
                      <DroppableBox
                        key={boxIndex}
                        box={box}
                        boxIndex={boxIndex}
                        onDrop={handleDrop}
                      />
                    ))}
                  </div>
                </div>
  
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                    marginTop: "20px"
                  }}
                >
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "10px",
                    borderTop: "1px solid #ccc",
                    borderBottom: "1px solid #ccc"
                  }}>
                    <span style={{
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "#333",
                      whiteSpace: "nowrap"
                    }}>
                      Suggest other category names by entering them in the box:
                    </span>
                    <input
                      type="text"
                      value={suggestionInput}
                      onChange={(e) => setSuggestionInput(e.target.value)}
                      placeholder="Enter category suggestion"
                      style={{
                        flex: "1",
                        padding: "8px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        fontSize: "14px"
                      }}
                    />
                    <button
                      style={{
                        padding: "8px 15px",
                        backgroundColor: "#007bff", // Changed to blue
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "bold",
                        whiteSpace: "nowrap"
                      }}
                      onClick={handleSuggestionSubmit}
                    >
                      Submit
                    </button>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    {!isFirstTime && (
                      <button
                        style={{
                          padding: "10px 20px",
                          backgroundColor: "#dc3545",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          onClose();
                          window.location.href = "/main";
                        }}
                      >
                        Exit
                      </button>
                    )}
                    <button
                      style={{
                        padding: "10px 20px",
                        backgroundColor: "#ffc107",
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        fontWeight: "bold",
                        cursor: "pointer",
                      }}
                      onClick={handleSubmit}
                    >
                      Save for Later
                    </button>
                    <button
                      style={{
                        padding: "10px 20px",
                        backgroundColor: "#28a745",
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        fontWeight: "bold",
                        cursor: "pointer",
                      }}
                      onClick={handleReset}
                    >
                      Reset
                    </button>
                    <button
                      style={{
                        padding: "10px 20px",
                        backgroundColor: "#28a745",
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        fontWeight: "bold",
                        cursor: "pointer",
                      }}
                      onClick={handleNextClick}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <Popup2 onClose={onClose} isFirstTime={isFirstTime}/>
      )}

      {showQuiz && (
        <Quiz onClose={() => setShowQuiz(false)} />
      )}
    </>
  );

  // Conditionally wrap the content in a DndProvider
  return noDndProvider ? (
    renderContent()
  ) : (
    <DndProvider backend={HTML5Backend}>
      {renderContent()}
    </DndProvider>
  );
}

export default Popup1;